import { App, Button, Listy, Spin, Tooltip } from 'antd';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState, type UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { FilesTableEnum } from '../../../enums/FilesTableEnum';
import { SpecialFolderTypeEnum } from '../../../enums/SpecialFolderTypeEnum';
import { useAuth } from '../../../hooks/useAuth';
import { getDefaultFiltersForListType } from '../../../hooks/useFileShortcutForm';
import type FailedFile from '../../../interfaces/FailedFile';
import type File from '../../../interfaces/File';
import type { GetFilesPayload } from '../../../interfaces/FileApiService';
import type User from '../../../interfaces/User';
import { FileService } from '../../../services/fileApiService';
import { useAppStore } from '../../../store/store';
import { handleFilePasswordError } from '../../../utils/filePasswordErrors';
import { partitionFilesByMoveResult } from '../../../utils/filesMoveResult';
import { FILE_PREVIEW_EXTENSIONS_IMAGES, getFileTitle, getFolderHex } from '../../../utils/utils';
import FileIcon from '../../Common/FileIcon';
import { LISTY_ITEM_NO_HOVER } from '../../Common/listyItemClassNames';
import TruncatedTooltip from '../../Common/TruncatedTooltip';
import { Icon } from '../../Core/Icon';
import { FilesMoveErrorForm } from '../FilesMoveErrorForm';
import {
  canCreateFolderInPicker,
  filterPickerFilesBySearch,
  FolderPickerBreadcrumb,
  FolderPickerCreateContextMenu,
  FolderPickerToolbar,
  useFolderPickerCreateFolder,
  useFolderPickerGlobalSearch,
} from '../FolderPicker';

const fileService = new FileService();

/** Fixed list area height in move modal folder picker. */
const FILE_MOVE_LIST_VIEWPORT_HEIGHT = 320;

const MOVE_LISTY_ITEM_CLASS = clsx(LISTY_ITEM_NO_HOVER, 'p-0!');
const MOVE_ROW_CLASS = 'flex flex-row items-center justify-between rounded px-2 py-2';
const MOVE_ROW_INNER_CLASS = 'flex min-w-0 flex-1 flex-row items-center gap-2';
const MOVE_ROW_TITLE_CLASS = 'w-full truncate';

interface RootPickerItem {
  id: 'personal' | 'received' | 'favorites';
  listType: 'personal' | 'received' | 'favorites';
  iconName: 'FolderIcon' | 'FileDownIcon' | 'StarIcon';
  labelKey: 'common.files' | 'common.received' | 'common.favorites';
}

const ROOT_PICKER_ITEMS: RootPickerItem[] = [
  { id: 'personal', listType: 'personal', iconName: 'FolderIcon', labelKey: 'common.files' },
  { id: 'received', listType: 'received', iconName: 'FileDownIcon', labelKey: 'common.received' },
  { id: 'favorites', listType: 'favorites', iconName: 'StarIcon', labelKey: 'common.favorites' },
];

interface Props {
  selectedFiles: File[];
  onCancel: () => void;
  onMoveComplete: (
    destinationFolderId: number | null,
    movedFiles: File[],
    failedFiles: FailedFile[],
  ) => void;
  currentFolder?: File | null;
  externalFilters?: Partial<GetFilesPayload>;
  authenticatedUser: User | null;
  /** Action mode: move (default) or convertToPdf */
  action?: 'move' | 'convertToPdf';
  /** Called on convert confirmation (convertToPdf mode only) */
  onConvertConfirm?: (destinationFolderId: number | null) => void;
}

function inferListTypeFromExternalFilters(
  externalFilters?: Partial<GetFilesPayload>,
): 'personal' | 'received' | 'favorites' {
  if (!externalFilters) return 'personal';
  if (externalFilters.type === 1) {
    return 'received';
  }
  if (externalFilters.list === 2) return 'favorites';
  return 'personal';
}

function buildFiltersFromExternal(
  externalFilters: Partial<GetFilesPayload> | undefined,
  listType: 'personal' | 'received' | 'favorites',
): GetFilesPayload {
  const base = getDefaultFiltersForListType(listType);
  return {
    ...base,
    orderAsc: externalFilters?.orderAsc ?? base.orderAsc,
    orderColumn: externalFilters?.orderColumn ?? base.orderColumn,
    ...(externalFilters?.list !== undefined ? { list: externalFilters.list } : {}),
    ...(externalFilters?.type !== undefined ? { type: externalFilters.type } : {}),
    offset: 0,
    parent: undefined,
  };
}

function resolveReceivedStartParentId(options: {
  currentFolder?: File | null;
  externalFilters?: Partial<GetFilesPayload>;
  receivedFolderUidFromRoute?: string;
}): string | undefined {
  if (options.currentFolder?.id != null) {
    return String(options.currentFolder.id);
  }

  const externalParentId = options.externalFilters?.parent?.trim();
  if (externalParentId) {
    return externalParentId;
  }

  const receivedFolderUidFromRoute = options.receivedFolderUidFromRoute?.trim();
  if (receivedFolderUidFromRoute) {
    return receivedFolderUidFromRoute;
  }

  return undefined;
}

function buildMoveFormFilters(
  externalFilters: Partial<GetFilesPayload> | undefined,
  listType: 'personal' | 'received' | 'favorites',
  options?: {
    isReceivedMoveContext?: boolean;
    receivedStartParentId?: string;
  },
): GetFilesPayload {
  const baseFilters = buildFiltersFromExternal(externalFilters, listType);

  if (options?.isReceivedMoveContext && options.receivedStartParentId) {
    return {
      ...baseFilters,
      offset: 0,
      parent: options.receivedStartParentId,
    };
  }

  return baseFilters;
}

export const FileMoveForm = ({
  selectedFiles,
  onMoveComplete,
  onCancel,
  externalFilters,
  currentFolder,
  authenticatedUser,
  action = 'move',
  onConvertConfirm,
}: Props) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const { isInFileRoute } = useAuth();
  const { folderUid: receivedFolderUidFromRoute } = useParams();
  const isOnReceivedFilesRoute = isInFileRoute(FilesTableEnum.Received);
  /** Received moves are scoped to Received only (route and/or list filters from the files table). */
  const isReceivedMoveContext =
    isOnReceivedFilesRoute || inferListTypeFromExternalFilters(externalFilters) === 'received';

  const receivedStartParentId = useMemo(
    () =>
      isReceivedMoveContext
        ? resolveReceivedStartParentId({
            currentFolder,
            externalFilters,
            receivedFolderUidFromRoute,
          })
        : undefined,
    [isReceivedMoveContext, currentFolder, externalFilters, receivedFolderUidFromRoute],
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const areSelectedFilesMine = useMemo(
    () => selectedFiles.every((f: File) => f?.ownerId === authenticatedUser?.id),
    [selectedFiles, authenticatedUser],
  );

  const [selectedListType, setSelectedListType] = useState<'personal' | 'received' | 'favorites'>(
    () => (isReceivedMoveContext ? 'received' : inferListTypeFromExternalFilters(externalFilters)),
  );

  const [filters, setFilters] = useState<GetFilesPayload>(() => {
    const initialListType = isReceivedMoveContext
      ? 'received'
      : inferListTypeFromExternalFilters(externalFilters);
    const initialReceivedStartParentId = isReceivedMoveContext
      ? resolveReceivedStartParentId({
          currentFolder,
          externalFilters,
          receivedFolderUidFromRoute,
        })
      : undefined;
    return buildMoveFormFilters(externalFilters, initialListType, {
      isReceivedMoveContext,
      receivedStartParentId: initialReceivedStartParentId,
    });
  });

  const [files, setFiles] = useState<File[]>([]);
  const [hasMore, setHasMore] = useState(false);
  // Convert: default destination = current folder or source file's parent (same-folder UX).
  const [selectedFolder, setSelectedFolder] = useState<File | null>(() => {
    if (action !== 'convertToPdf') {
      return null;
    }
    if (currentFolder) {
      return currentFolder;
    }
    const parentId = selectedFiles[0]?.parentId;
    if (parentId != null && parentId > 0) {
      return { id: parentId, isFolder: true, fileName: '' } as File;
    }
    return null;
  });
  /** Root as destination when highlighted like FileShortcutForm (`Files` row selected). */
  const [isFilesRootSelected, setIsFilesRootSelected] = useState(false);
  const [parent, setParent] = useState<File | undefined>();
  /** Until true, stay on root picker rows only — no folder listing fetch (matches FileShortcutForm). */
  const [isNavigatingIntoFolder, setIsNavigatingIntoFolder] = useState(isReceivedMoveContext);
  const [isMoving, setIsMoving] = useState(false);
  const [failedFilesAfterMove, setFailedFilesAfterMove] = useState<FailedFile[]>([]);
  const [pendingDestinationFolderId, setPendingDestinationFolderId] = useState<number | null>(null);

  const pendingFilePasswordRequest = useAppStore((s) => s.pendingFilePasswordRequest);
  const hadPendingRequestRef = useRef(false);
  const {
    getFilePasswordSession,
    setPendingFilePasswordRequest,
    setPendingFilePasswordFromMoveForm,
    setPendingFilePasswordFromUpload,
  } = useAppStore();

  const previousParentRef = useRef<string | undefined>(undefined);
  const targetFolderIdRef = useRef<number | null>(null);
  const folderToSelectAfterPasswordRef = useRef<File | null>(null);
  const fromApiErrorRef = useRef<boolean>(false);

  const filesFetchGenerationRef = useRef(0);
  const loadMoreLockedRef = useRef(false);

  const memoizedListFilters = useMemo(
    () => ({
      count: filters.count,
      offset: filters.offset,
      orderAsc: filters.orderAsc,
      orderColumn: filters.orderColumn,
      parent: filters.parent,
    }),
    [filters.count, filters.offset, filters.orderAsc, filters.orderColumn, filters.parent],
  );

  const selectedFileIdsKey = useMemo(
    () => selectedFiles.map((f) => f.id).join(','),
    [selectedFiles],
  );

  const listFetchKey = useMemo(
    () => `${selectedFileIdsKey}:${filters.parent ?? ''}:${selectedListType}`,
    [selectedFileIdsKey, filters.parent, selectedListType],
  );

  const isAtRootLevel = useMemo(() => {
    return (!filters.parent || filters.parent === '') && !isNavigatingIntoFolder;
  }, [filters.parent, isNavigatingIntoFolder]);

  /** Top of Received tree in move modal: no parent folder, back navigation not applicable. */
  const isAtReceivedListRoot = useMemo(
    () =>
      isReceivedMoveContext &&
      isNavigatingIntoFolder &&
      (!filters.parent || filters.parent.trim() === ''),
    [isReceivedMoveContext, isNavigatingIntoFolder, filters.parent],
  );

  useEffect(() => {
    setIsFilesRootSelected(false);
    setSelectedFolder(null);
    const nextListType = isReceivedMoveContext
      ? 'received'
      : inferListTypeFromExternalFilters(externalFilters);
    setSelectedListType(nextListType);
    setIsNavigatingIntoFolder(isReceivedMoveContext);
    setFilters(
      buildMoveFormFilters(externalFilters, nextListType, {
        isReceivedMoveContext,
        receivedStartParentId,
      }),
    );
  }, [selectedFiles, externalFilters, isReceivedMoveContext, receivedStartParentId]);

  /**
   * Non-owner shortcut parity: keep list scoped when sitting on root picker (mirrors useFileShortcutForm).
   */
  useEffect(() => {
    if (!areSelectedFilesMine && isAtRootLevel && !isReceivedMoveContext) {
      setSelectedFolder(null);
      setIsNavigatingIntoFolder(false);
      setIsFilesRootSelected(false);
      setFilters((prev) => ({
        ...prev,
        list: 0,
        offset: 0,
        parent: undefined,
      }));
    }
  }, [selectedListType, areSelectedFilesMine, isAtRootLevel, isReceivedMoveContext]);

  /**
   * Loads folder listing only after leaving root picker — same boundary as FileShortcutForm.
   */
  useEffect(() => {
    if (isAtRootLevel) {
      setFiles([]);
      setParent(undefined);
      setHasMore(false);
      loadMoreLockedRef.current = false;
      return;
    }

    const fetchGeneration = ++filesFetchGenerationRef.current;
    const requestOffset = memoizedListFilters.offset;
    if (requestOffset === 0) {
      setFiles([]);
      setHasMore(false);
    }

    const parentForRequest = memoizedListFilters.parent;
    const hasParent = !!(parentForRequest && parentForRequest.trim() !== '');
    const payloadList = hasParent ? 0 : selectedListType === 'favorites' ? 2 : 0;
    const searchQuery = (filters.search ?? '').trim();
    const payload: GetFilesPayload = {
      ...filters,
      ...memoizedListFilters,
      list: payloadList,
      parent: parentForRequest || undefined,
      type: selectedListType === 'received' ? 1 : 2,
      // Omit empty string — backend treats missing search as no filter (iOS sends nil).
      search: searchQuery || undefined,
    };

    setIsLoading(true);
    void fileService
      .getFiles(payload)
      .then((res) => {
        if (fetchGeneration !== filesFetchGenerationRef.current) {
          return;
        }
        const page = res.myFiles ?? [];
        setFiles((prevFiles) => {
          const allFiles = requestOffset === 0 ? page : [...prevFiles, ...page];
          const filesById = new Map<number, File>();
          allFiles.forEach((file) => filesById.set(file.id, file));
          return Array.from(filesById.values());
        });
        setParent(res?.parent || undefined);
        setHasMore(requestOffset + page.length < (res.total ?? 0));
      })
      .catch((error) => {
        if (fetchGeneration !== filesFetchGenerationRef.current) {
          return;
        }
        if (handleFilePasswordError(error)) {
          fromApiErrorRef.current = true;
          setPendingFilePasswordFromMoveForm(true);
          setFiles([]);
          setParent(undefined);
          setHasMore(false);
          return;
        }
        notification.error({
          title: t('errors.genericFetchError', {
            resource: t('common.files'),
          }),
        });
      })
      .finally(() => {
        if (fetchGeneration === filesFetchGenerationRef.current) {
          setIsLoading(false);
          loadMoreLockedRef.current = false;
        }
      });
  }, [
    isAtRootLevel,
    listFetchKey,
    memoizedListFilters,
    selectedListType,
    filters.count,
    filters.fileTypes,
    filters.onlySigned,
    filters.onlyUnsealed,
    filters.priceFilter,
    filters.search,
    filters.shareWith,
    filters.tags,
    filters.list,
    filters.type,
    notification,
    setPendingFilePasswordFromMoveForm,
    t,
  ]);

  const canCreateFolder = useMemo(
    () =>
      canCreateFolderInPicker({
        isAtRootLevel,
        listType: selectedListType,
        parentFolder: parent ?? null,
        authenticatedUserId: authenticatedUser?.id,
      }),
    [authenticatedUser?.id, isAtRootLevel, parent, selectedListType],
  );

  const getCreateParentId = useCallback(() => filters.parent || undefined, [filters.parent]);

  const [exactMatch, setExactMatch] = useState(false);

  const handleSearchChange = useCallback(
    (search: string) => {
      // Avoid empty-state flash before fetch effect runs (client filter can empty list first).
      if (!isAtRootLevel) {
        setIsLoading(true);
      }
      setFilters((prev) => ({ ...prev, search, offset: 0 }));
    },
    [isAtRootLevel],
  );

  const { globalSearchResults, isGlobalSearching, isGlobalSearchActive } =
    useFolderPickerGlobalSearch({
      enabled: isAtRootLevel,
      keyword: filters.search ?? '',
      exactMatch,
    });

  const visibleFiles = useMemo(
    () => (isAtRootLevel ? globalSearchResults : filterPickerFilesBySearch(files, filters.search)),
    [files, filters.search, globalSearchResults, isAtRootLevel],
  );

  const listRootLabel = useMemo(() => {
    if (selectedListType === 'received') return t('common.received');
    if (selectedListType === 'favorites') return t('common.favorites');
    return t('common.files');
  }, [selectedListType, t]);

  const handleBreadcrumbListRootClick = useCallback(() => {
    if (areSelectedFilesMine || selectedListType === 'personal') {
      setFilters(buildFiltersFromExternal(externalFilters, 'personal'));
      setSelectedFolder(null);
      setIsNavigatingIntoFolder(false);
      setIsFilesRootSelected(false);
      setSelectedListType('personal');
    } else if (selectedListType === 'received') {
      setFilters(buildFiltersFromExternal(externalFilters, 'received'));
      setSelectedFolder(null);
      setIsNavigatingIntoFolder(false);
      setIsFilesRootSelected(false);
      setSelectedListType('received');
    } else if (selectedListType === 'favorites') {
      setFilters(buildFiltersFromExternal(externalFilters, 'favorites'));
      setSelectedFolder(null);
      setIsNavigatingIntoFolder(false);
      setIsFilesRootSelected(false);
      setSelectedListType('favorites');
    }
  }, [areSelectedFilesMine, externalFilters, selectedListType]);

  const handleBreadcrumbPathNavigate = useCallback((fileId: string) => {
    setFilters((prev) => ({ ...prev, offset: 0, parent: fileId }));
  }, []);

  const handleScroll = useCallback(
    (event?: UIEvent<HTMLElement>) => {
      const scrollElement = event?.currentTarget;
      if (!scrollElement) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      const nearBottom = scrollTop + clientHeight >= scrollHeight - 50;
      if (nearBottom && hasMore && !isLoading && !loadMoreLockedRef.current) {
        loadMoreLockedRef.current = true;
        setFilters((prev) => ({ ...prev, offset: prev.offset + prev.count }));
      }
    },
    [hasMore, isLoading],
  );

  const openFolderOrRequestPassword = useCallback(
    (f: File) => {
      if (!f.isFolder) return;
      if (f.isPasswordSet && !f.isPasswordBypass && !getFilePasswordSession(f.id)) {
        folderToSelectAfterPasswordRef.current = null;
        targetFolderIdRef.current = f.id;
        fromApiErrorRef.current = false;
        previousParentRef.current = filters.parent;
        setPendingFilePasswordFromMoveForm(true);
        setPendingFilePasswordRequest({
          fileId: f.id,
          fileName: f.fileName,
        });
        return;
      }
      previousParentRef.current = filters.parent;
      setIsFilesRootSelected(false);
      setSelectedFolder(f);
      setFilters((prev) => ({ ...prev, search: '', offset: 0, parent: f.id.toString() }));
    },
    [
      filters,
      getFilePasswordSession,
      setPendingFilePasswordRequest,
      setPendingFilePasswordFromMoveForm,
    ],
  );

  const { openCreateFolder } = useFolderPickerCreateFolder({
    getParentId: getCreateParentId,
    onCreated: openFolderOrRequestPassword,
  });

  const selectFolderOrRequestPassword = useCallback(
    (f: File) => {
      if (!f.isFolder) return;
      if (f.isPasswordSet && !f.isPasswordBypass && !getFilePasswordSession(f.id)) {
        folderToSelectAfterPasswordRef.current = { ...f, path: parent?.path };
        targetFolderIdRef.current = f.id;
        fromApiErrorRef.current = false;
        setPendingFilePasswordFromMoveForm(true);
        setPendingFilePasswordRequest({
          fileId: f.id,
          fileName: f.fileName,
        });
        return;
      }
      setIsFilesRootSelected(false);
      setSelectedFolder({ ...f, path: parent?.path });
    },
    [
      parent?.path,
      getFilePasswordSession,
      setPendingFilePasswordRequest,
      setPendingFilePasswordFromMoveForm,
    ],
  );

  const currentFolderParent = useMemo(() => {
    if (parent?.path && parent.path.length > 0) {
      const path = parent.path;
      return {
        ...path[path.length - 2],
        title: path?.[path.length - 1]?.title,
      };
    }
    return null;
  }, [parent]);

  const isValid = useMemo(() => {
    if (selectedFolder != null) {
      return action === 'convertToPdf' || currentFolder?.id !== selectedFolder.id;
    }
    if (isFilesRootSelected && selectedListType === 'personal') {
      if (action === 'convertToPdf') {
        return true;
      }
      if (currentFolder === null) {
        return false;
      }
      return true;
    }
    return false;
  }, [action, currentFolder, selectedFolder, isFilesRootSelected, selectedListType]);

  const selectedFileIds = useMemo(() => selectedFiles?.map((f) => f.id), [selectedFiles]);

  const isFileDisabled = useCallback(
    (file: File) => {
      if (
        [SpecialFolderTypeEnum.QuickShare, SpecialFolderTypeEnum.AiGrant].includes(
          file.specialFolder,
        )
      ) {
        return true;
      }
      if (!file.isFolder) {
        return true;
      }
      /** Same rules as {@link FileShortcutForm} / {@link useFileShortcutForm}: writable received folders accept drops/moves. */
      if (selectedListType === 'received' && file.isReadOnly) {
        return true;
      }
      if (selectedListType === 'personal' && file.isReadOnly) {
        return true;
      }
      if (selectedFileIds.includes(file.id)) {
        return true;
      }
      /** Personal tree only: destinations must belong to the same owner as what is being moved. */
      if (
        selectedListType === 'personal' &&
        selectedFiles.some((f) => f?.ownerId !== file?.ownerId)
      ) {
        return true;
      }
      return false;
    },
    [selectedFiles, selectedFileIds, selectedListType],
  );

  useEffect(() => {
    if (pendingFilePasswordRequest) {
      hadPendingRequestRef.current = true;
    } else if (hadPendingRequestRef.current) {
      hadPendingRequestRef.current = false;
      const fromUpload = useAppStore.getState().pendingFilePasswordFromUpload;
      if (fromUpload) {
        folderToSelectAfterPasswordRef.current = null;
        targetFolderIdRef.current = null;
        fromApiErrorRef.current = false;
        setPendingFilePasswordFromUpload(false);
        setPendingFilePasswordFromMoveForm(false);
        return;
      }
      const targetId = targetFolderIdRef.current;
      const fromApi = fromApiErrorRef.current;
      const folderToSelect = folderToSelectAfterPasswordRef.current;

      if (targetId != null && getFilePasswordSession(targetId)) {
        setIsNavigatingIntoFolder(true);
        if (folderToSelect != null && folderToSelect.id === targetId) {
          setIsFilesRootSelected(false);
          setSelectedFolder(folderToSelect);
          folderToSelectAfterPasswordRef.current = null;
        } else {
          setIsFilesRootSelected(false);
          setFilters((prev) => ({ ...prev, offset: 0, parent: String(targetId) }));
        }
      } else if (
        fromApi &&
        filters.parent &&
        getFilePasswordSession(parseInt(String(filters.parent), 10))
      ) {
        setIsNavigatingIntoFolder(true);
        setFiles([]);
        setFilters((prev) => ({ ...prev, offset: 0, parent: filters.parent }));
      } else {
        const revertParent = fromApi ? undefined : previousParentRef.current;
        setFilters((prev) => ({
          ...prev,
          offset: 0,
          parent: revertParent,
        }));
        setIsFilesRootSelected(false);
        setSelectedFolder(null);
        if (!revertParent || revertParent === '') {
          setIsNavigatingIntoFolder(isReceivedMoveContext);
        }
      }

      folderToSelectAfterPasswordRef.current = null;
      targetFolderIdRef.current = null;
      fromApiErrorRef.current = false;
      setPendingFilePasswordFromMoveForm(false);
    }
  }, [
    pendingFilePasswordRequest,
    filters.parent,
    getFilePasswordSession,
    setPendingFilePasswordFromMoveForm,
    setPendingFilePasswordFromUpload,
    isReceivedMoveContext,
  ]);

  const navigateIntoListRoot = useCallback(
    (listType: 'personal' | 'received' | 'favorites') => {
      setIsFilesRootSelected(false);
      setSelectedListType(listType);
      setIsNavigatingIntoFolder(true);
      setFilters(buildFiltersFromExternal(externalFilters, listType));
    },
    [externalFilters],
  );

  const handleMove = useCallback(async () => {
    const destinationFolderId = selectedFolder?.id ?? null;

    // Convert to PDF mode: just call the confirmation callback
    if (action === 'convertToPdf' && onConvertConfirm) {
      onConvertConfirm(destinationFolderId);
      return;
    }

    // Move mode: execute the move API call
    setIsMoving(true);
    try {
      const response = await fileService.moveFiles(
        destinationFolderId,
        selectedFiles.map((file) => file.cufi),
      );
      const { movedFiles, failedFiles } = partitionFilesByMoveResult(
        selectedFiles,
        response?.failedFilesList,
      );

      if (failedFiles.length > 0) {
        setFailedFilesAfterMove(failedFiles);
        setPendingDestinationFolderId(destinationFolderId);
        return;
      }

      onMoveComplete(destinationFolderId, movedFiles, []);
    } catch {
      notification.error({
        title: t('errors.errorMoveFile'),
      });
    } finally {
      setIsMoving(false);
    }
  }, [
    action,
    notification,
    onConvertConfirm,
    onMoveComplete,
    selectedFiles,
    selectedFolder?.id,
    t,
  ]);

  const handleCloseMoveErrors = useCallback(() => {
    const { movedFiles } = partitionFilesByMoveResult(selectedFiles, failedFilesAfterMove);
    onMoveComplete(pendingDestinationFolderId, movedFiles, failedFilesAfterMove);
  }, [failedFilesAfterMove, onMoveComplete, pendingDestinationFolderId, selectedFiles]);

  const renderRootRow = useCallback(
    (item: RootPickerItem) => {
      const label = t(item.labelKey);
      const isSelected =
        item.listType === 'personal'
          ? !selectedFolder && selectedListType === 'personal' && isFilesRootSelected
          : selectedListType === item.listType && !selectedFolder;

      return (
        <div
          className={clsx(
            MOVE_ROW_CLASS,
            'text-blue-dark hover:bg-gray cursor-pointer',
            isSelected && 'bg-light',
          )}
          onClick={() => {
            setSelectedFolder(null);
            setSelectedListType(item.listType);
            setIsNavigatingIntoFolder(false);
            setIsFilesRootSelected(item.listType === 'personal');
          }}
          onDoubleClick={() => {
            if (item.listType === 'personal') {
              setSelectedFolder(null);
            }
            navigateIntoListRoot(item.listType);
          }}
        >
          <div className={MOVE_ROW_INNER_CLASS}>
            <Icon name={item.iconName} className="h-6 w-6 shrink-0" />
            <span className={clsx(MOVE_ROW_TITLE_CLASS, 'text-azure')}>{label}</span>
          </div>
          <div className="ml-auto shrink-0">
            <Button
              type="text"
              aria-label={t('a11y.openList', { name: label })}
              onClick={(e) => {
                e.stopPropagation();
                navigateIntoListRoot(item.listType);
              }}
            >
              <Icon name="ChevronRightIcon" className="w-5" />
            </Button>
          </div>
        </div>
      );
    },
    [
      isFilesRootSelected,
      navigateIntoListRoot,
      selectedFolder,
      selectedListType,
      setIsFilesRootSelected,
      setIsNavigatingIntoFolder,
      setSelectedFolder,
      setSelectedListType,
      t,
    ],
  );

  const renderFolderRow = useCallback(
    (f: File) => {
      // Move destinations are folders only — files always disabled (incl. global search hits).
      const disabled = isFileDisabled(f);

      return (
        <div
          className={clsx(
            MOVE_ROW_CLASS,
            disabled ? 'pointer-events-none opacity-50' : 'hover:bg-gray cursor-pointer',
            selectedFolder?.id === f.id && 'bg-light',
          )}
          aria-disabled={disabled || undefined}
          onDoubleClick={() => {
            if (!disabled && f.isFolder) {
              openFolderOrRequestPassword(f);
            }
          }}
          onClick={() => {
            if (!disabled && f.isFolder) {
              selectFolderOrRequestPassword(f);
            }
          }}
        >
          <div className={MOVE_ROW_INNER_CLASS}>
            <FileIcon
              fileId={f.id}
              allowedFilePreviewExtensions={FILE_PREVIEW_EXTENSIONS_IMAGES}
              isFolder={f.isFolder}
              isFolderPasswordProtected={f.isPasswordSet && !f.isPasswordBypass}
              fileExtension={f.fileExtension}
              className="h-6 w-6 shrink-0"
              style={{
                color: getFolderHex(f),
              }}
            />
            <span className={MOVE_ROW_TITLE_CLASS}>
              <TruncatedTooltip>{getFileTitle(f)}</TruncatedTooltip>
            </span>
          </div>
          <div className="ml-auto shrink-0">
            {f.isFolder && (
              <Button
                type="text"
                aria-label={t('a11y.openFolder', { name: getFileTitle(f) })}
                onClick={(e) => {
                  e.stopPropagation();
                  openFolderOrRequestPassword(f);
                }}
              >
                <Icon name="ChevronRightIcon" className="w-5" />
              </Button>
            )}
          </div>
        </div>
      );
    },
    [
      isFileDisabled,
      openFolderOrRequestPassword,
      selectFolderOrRequestPassword,
      selectedFolder?.id,
      t,
    ],
  );

  if (failedFilesAfterMove.length > 0) {
    return (
      <FilesMoveErrorForm failedFilesList={failedFilesAfterMove} onClose={handleCloseMoveErrors} />
    );
  }

  const isSearchBusy = (isGlobalSearchActive && isGlobalSearching) || (!isAtRootLevel && isLoading);
  // Spinner only when busy with nothing to show; keep prior results while refetching.
  const isInitialLoading = isSearchBusy && visibleFiles.length === 0;
  const isLoadingMore = isLoading && files.length > 0 && !isAtRootLevel;
  const showRootPicker = isAtRootLevel && !isGlobalSearchActive;
  const showSearchOrFolderList = !showRootPicker;
  const showEmpty = visibleFiles.length === 0 && !isSearchBusy && showSearchOrFolderList;

  const formTitle =
    action === 'convertToPdf' ? t('modal.convertToPdf.title') : t('a11y.fileMoveForm');
  const confirmButtonLabel = action === 'convertToPdf' ? t('common.convert') : t('common.move');

  return (
    <div aria-label={formTitle}>
      <div className="text-gray-dark text-sm">
        <span>
          {action === 'convertToPdf'
            ? t('modal.convertToPdf.selectDestination')
            : t('modal.fileMoveForm.currentPosition')}
          :
        </span>
        <span className="ml-1">{currentFolder?.fileName || t('common.files')}</span>
      </div>

      <div
        className={clsx(
          'flex flex-row items-center pt-2',
          isAtRootLevel && 'pointer-events-none opacity-0',
        )}
      >
        {!isAtReceivedListRoot && (
          <Tooltip title={t('modal.fileShortcutForm.goBack')}>
            <Button
              type="text"
              className="p-0!"
              aria-label={t('a11y.goBack')}
              onClick={() => {
                if (isAtRootLevel) return;
                setSelectedFolder(null);
                setIsFilesRootSelected(false);
                if (currentFolderParent) {
                  setFilters((prev) => ({
                    ...prev,
                    offset: 0,
                    parent: currentFolderParent.fileId || undefined,
                  }));
                } else {
                  setFilters(buildFiltersFromExternal(externalFilters, selectedListType));
                  setIsNavigatingIntoFolder(isReceivedMoveContext);
                }
              }}
            >
              <Icon name="ChevronLeftIcon" className="h-6 w-6" />
            </Button>
          </Tooltip>
        )}
        <span
          className={clsx(!isAtReceivedListRoot && 'ml-1', isAtReceivedListRoot && 'pt-1 pb-1.5')}
        >
          {isAtReceivedListRoot
            ? t('common.received')
            : isAtRootLevel
              ? selectedListType === 'received'
                ? t('common.received')
                : selectedListType === 'favorites'
                  ? t('common.favorites')
                  : t('common.files')
              : currentFolderParent?.title}
        </span>
      </div>
      <div className="border-gray-disabled border-b" />

      <FolderPickerToolbar
        search={filters.search ?? ''}
        onSearchChange={handleSearchChange}
        searchMode={isAtRootLevel ? 'global' : 'inFolder'}
        exactMatch={exactMatch}
        onExactMatchChange={setExactMatch}
        canCreateFolder={canCreateFolder}
        onCreateFolder={openCreateFolder}
      />

      <div className="relative min-h-[320px]" style={{ height: FILE_MOVE_LIST_VIEWPORT_HEIGHT }}>
        {showEmpty ? (
          <FolderPickerCreateContextMenu
            enabled={canCreateFolder}
            onCreateFolder={openCreateFolder}
          >
            <div className="flex h-full items-center justify-center overflow-hidden">
              <div className="flex flex-col items-center justify-center gap-2">
                <Icon name="FolderIcon" className="h-36 w-36 opacity-25" />
                <span className="text-gray-dark text-sm">
                  {isGlobalSearchActive
                    ? t('common.noFilesFound')
                    : t('modal.fileMoveForm.folderIsEmpty')}
                </span>
              </div>
            </div>
          </FolderPickerCreateContextMenu>
        ) : isInitialLoading ? (
          <div className="flex h-full items-center justify-center" aria-busy="true">
            <Spin />
          </div>
        ) : showRootPicker ? (
          <Listy
            items={ROOT_PICKER_ITEMS}
            rowKey="id"
            height={FILE_MOVE_LIST_VIEWPORT_HEIGHT}
            classNames={{ item: MOVE_LISTY_ITEM_CLASS }}
            itemRender={renderRootRow}
          />
        ) : (
          <FolderPickerCreateContextMenu
            enabled={canCreateFolder}
            onCreateFolder={openCreateFolder}
          >
            <>
              <Listy
                items={visibleFiles}
                rowKey="id"
                virtual
                height={FILE_MOVE_LIST_VIEWPORT_HEIGHT}
                onScroll={handleScroll}
                classNames={{ item: MOVE_LISTY_ITEM_CLASS }}
                itemRender={renderFolderRow}
              />
              {isLoadingMore && (
                <div className="pointer-events-none absolute right-0 bottom-0 left-0 flex justify-center pb-2">
                  <Spin size="small" />
                </div>
              )}
            </>
          </FolderPickerCreateContextMenu>
        )}
      </div>

      <div className="border-gray-disabled border-t" />
      <div className="min-w-0 overflow-hidden pt-2 text-sm">
        {isFilesRootSelected && !selectedFolder ? (
          <span>{t('common.files')}</span>
        ) : !selectedFolder && !isAtRootLevel ? (
          <span>{t('modal.fileMoveForm.selectFolderToMoveInto')}</span>
        ) : !selectedFolder && isAtRootLevel && !isFilesRootSelected ? (
          <span>{t('modal.fileMoveForm.selectFolderToMoveInto')}</span>
        ) : (
          <FolderPickerBreadcrumb
            listRootLabel={listRootLabel}
            onListRootClick={handleBreadcrumbListRootClick}
            path={selectedFolder?.path}
            currentTitle={selectedFolder?.fileName}
            currentId={selectedFolder?.id}
            onPathNavigate={handleBreadcrumbPathNavigate}
          />
        )}
      </div>

      <div className="mt-4 flex flex-row items-center justify-end gap-2">
        <Button size="middle" onClick={() => onCancel()}>
          {t('common.cancel')}
        </Button>
        <Button
          size="middle"
          type="primary"
          loading={isMoving}
          disabled={
            isMoving ||
            (!selectedFolder &&
              !(isAtRootLevel && selectedListType === 'personal' && isFilesRootSelected)) ||
            !isValid
          }
          onClick={() => {
            void handleMove();
          }}
        >
          {confirmButtonLabel}
        </Button>
      </div>
    </div>
  );
};

export default FileMoveForm;
